import { type ButtonProps, buttonStyle } from '../Button'
import { List, ListItem, type ListItemProps, type ListProps } from '../List'
import { visuallyHidden } from '../util.css'
import { Checkbox, type CheckboxProps } from './Checkbox'
import { radioTab, radioTabsList } from './RadioTabs.css'
import type { ChangeEvent, JSXElementConstructor, ReactElement } from 'react'

export type OptionValueTypes = string | boolean
export type OptionLabelTypes =
  | string
  | ReactElement<any, string | JSXElementConstructor<any>>

type DropdownOption<
  T extends OptionValueTypes = string,
  K extends OptionLabelTypes = string,
> = {
  [key: string]: unknown
  itemProps?: ListItemProps // Allow passing eg. className or data attribute
  value: T
  label: K
  disabled?: boolean
}

export type RadioTabOption<
  T extends OptionValueTypes,
  K extends OptionLabelTypes = OptionLabelTypes,
> = DropdownOption<T, K> &
  Partial<Omit<CheckboxProps, 'size' | keyof DropdownOption<T, K>>> & {
    tooltipId?: string
    tooltip?: string
  }

interface RadioTabsProps<
  T extends OptionValueTypes,
  K extends OptionLabelTypes,
> {
  options: RadioTabOption<T, K>[]
  name: string
  size?: ButtonProps['size']
  disabled?: boolean
  // Allows controlled and uncontrolled modes
  onSelect?: (
    option: DropdownOption<T, K>,
    e: ChangeEvent<HTMLInputElement>,
  ) => void
  value?: T
  defaultValue?: T
}

export const RadioTabs = <
  T extends OptionValueTypes,
  K extends OptionLabelTypes,
>({
  options,
  value,
  name,
  onSelect,
  size = 'small',
  disabled,
  className,
  defaultValue,
  ...listBoxProps
}: RadioTabsProps<T, K> & Omit<ListProps, keyof RadioTabsProps<T, K>>) => {
  return (
    <List
      className={[radioTabsList, className]}
      unstyled
      flexShrink="0"
      {...listBoxProps}
    >
      {options.map((option, i) => {
        const id = `${name}-${i}`
        const { tooltipId, tooltip } = option
        const isDisabled = option.disabled ?? disabled
        const checked =
          typeof value === 'undefined' ? undefined : value === option.value
        const defaultChecked =
          typeof value === 'undefined'
            ? defaultValue === option.value
            : undefined

        const inputContent = (
          <Checkbox
            disabled={isDisabled}
            fontSize="xs"
            color="secondary"
            unstyled
            unspaced
            name={name}
            id={id}
            checked={checked}
            defaultChecked={defaultChecked}
            type="radio"
            className={[buttonStyle({ variant: 'ghost', size }), radioTab]}
            inputClassName={visuallyHidden}
            refToLabel
            value={option.value as string}
            onChange={
              onSelect
                ? (e) => {
                    if (!isDisabled && !checked) {
                      onSelect(option, e)
                    } else {
                      e.preventDefault()
                    }
                  }
                : undefined
            }
          >
            {option.label}
          </Checkbox>
        )

        return (
          <ListItem key={id} data-tooltip={tooltip}>
            {inputContent}
          </ListItem>
        )
      })}
    </List>
  )
}
